import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaStar, FaUser, FaUsers, FaCar, FaBox } from 'react-icons/fa';
import Spinner from '../common/Spinner';
import './MyProfile.css';

const UserStats = ({ stats, activePosts, activePackages, archivedPosts, archivedPackages, cancellationStats, isLoading }) => {
  const { t } = useTranslation('common');

  if (isLoading) {
    return (
      <div className="Stats-box loading">
        <Spinner size="medium" />
      </div>
    );
  }

  // Calculate statistics
  const driverPosts = [...(activePosts || []), ...(archivedPosts || [])].filter(post => post.role === 'Driver').length;
  const passengerPosts = [...(activePosts || []), ...(archivedPosts || [])].filter(post => post.role === 'Passenger').length;
  const totalPackages = (activePackages?.length || 0) + (archivedPackages?.length || 0);
  const totalPosts = (activePosts?.length || 0) + (archivedPosts?.length || 0);

  return (
    <>
      <div className="Stats-box">
        <div className="AsDriver">
          <p>{t('stats.as_driver')}:</p>
          <h2>{driverPosts}</h2>
        </div>
        <div className="AsPassenger">
          <p>{t('stats.as_passenger')}:</p>
          <h2>{passengerPosts}</h2>
        </div>
        <div className="AsPassenger">
          <p>{t('stats.packages')}:</p>
          <h2>{totalPackages}</h2>
        </div>
        <div className="Posts">
          <p>{t('stats.total_posts')}:</p>
          <h2>{totalPosts + totalPackages}</h2>
        </div>
      </div>

      {/* Impact statistics */}
      {stats && (
        <div className="user-stats">
          <h2>{t('stats.your_impact')}</h2>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-label">{t('stats.total_distance_offered')}:</span>
              <span className="stat-value">{stats.total_distance_offered_km || 0} km</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">{t('stats.total_distance_shared')}:</span>
              <span className="stat-value">{stats.total_distance_shared_km || 0} km</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">{t('stats.total_rides_offered')}:</span>
              <span className="stat-value">{stats.total_rides_offered || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">{t('stats.total_rides_shared')}:</span>
              <span className="stat-value">{stats.total_rides_shared || 0}</span>
            </div>
            
            <div className="stat-item">
              <span className="stat-label">{t('stats.packages_sent')}:</span>
              <span className="stat-value">{stats.total_packages_sent || 0}</span>
            </div>
            
            <div className="stat-item">
              <span className="stat-label">{t('stats.packages_delivered')}:</span>
              <span className="stat-value">{stats.total_packages_delivered || 0}</span>
            </div>
            
            <div className="stat-item">
              <span className="stat-label">{t('stats.package_distance_sent')}:</span>
              <span className="stat-value">{stats.total_package_distance_km || 0} km</span>
            </div>
            
            <div className="stat-item">
              <span className="stat-label">{t('stats.delivery_distance')}:</span>
              <span className="stat-value">{stats.total_delivery_distance_km || 0} km</span>
            </div>
            {stats.average_rating && (
              <div className="stat-item">
                <span className="stat-label">{t('stats.rating')}:</span>
                <span className="stat-value">
                  {parseFloat(stats.average_rating).toFixed(1)}
                  <FaStar className="star-icon" />
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cancellation statistics */}
      {cancellationStats && (
        <div className="user-stats">
          <h2>{t('stats.cancellation_history')}</h2>
          <div className="stats-grid">
            {/* Package cancellation stats */}
            <div className="stat-item">
              <span className="stat-label">{t('stats.packages_cancelled')}:</span>
              <span className="stat-value">{cancellationStats.package_stats?.cancelled_total || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">{t('stats.packages_declined')}:</span>
              <span className="stat-value">{cancellationStats.package_stats?.declined || 0}</span>
            </div>
            
            {/* Ride cancellation stats */}
            <div className="stat-item">
              <span className="stat-label">{t('stats.rides_cancelled')}:</span>
              <span className="stat-value">{cancellationStats.ride_stats?.cancelled_total || 0}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">{t('stats.rides_declined')}:</span>
              <span className="stat-value">{cancellationStats.ride_stats?.declined || 0}</span>
            </div>
            
            {/* No-show stats */}
            <div className="stat-item">
              <span className="stat-label">{t('stats.total_no_shows')}:</span>
              <span className="stat-value">{cancellationStats.total_no_shows || 0}</span>
            </div>
            
            {/* Summary stats */}
            <div className="stat-item">
              <span className="stat-label">{t('stats.total_cancellations')}:</span>
              <span className="stat-value">{cancellationStats.total_cancellations || 0}</span>
            </div>
          </div>
          
          {/* Detailed breakdown (collapsible) */}
          {(cancellationStats.package_stats?.cancelled_total > 0 || cancellationStats.ride_stats?.cancelled_total > 0) && (
            <details className="cancellation-details">
              <summary>{t('stats.detailed_breakdown')}</summary>
              <div className="stats-grid detailed">
                {cancellationStats.package_stats?.cancelled_total > 0 && (
                  <>
                    <div className="stat-item">
                      <span className="stat-label">{t('stats.packages_cancelled_as_sender')}:</span>
                      <span className="stat-value">{cancellationStats.package_stats?.cancelled_as_sender || 0}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">{t('stats.packages_cancelled_as_carrier')}:</span>
                      <span className="stat-value">{cancellationStats.package_stats?.cancelled_as_carrier || 0}</span>
                    </div>
                  </>
                )}
                {cancellationStats.ride_stats?.cancelled_total > 0 && (
                  <>
                    <div className="stat-item">
                      <span className="stat-label">{t('stats.rides_cancelled_as_driver')}:</span>
                      <span className="stat-value">{cancellationStats.ride_stats?.cancelled_as_driver || 0}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">{t('stats.rides_cancelled_as_passenger')}:</span>
                      <span className="stat-value">{cancellationStats.ride_stats?.cancelled_as_passenger || 0}</span>
                    </div>
                  </>
                )}
                {cancellationStats.total_no_shows > 0 && (
                  <>
                    <div className="stat-item">
                      <span className="stat-label">{t('stats.package_no_shows')}:</span>
                      <span className="stat-value">{cancellationStats.package_stats?.no_shows || 0}</span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">{t('stats.ride_no_shows')}:</span>
                      <span className="stat-value">{cancellationStats.ride_stats?.no_shows || 0}</span>
                    </div>
                  </>
                )}
              </div>
            </details>
          )}
        </div>
      )}
    </>
  );
};

export default UserStats;